import { Alert } from 'src/design/atoms/Alert';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import {
  IconArchive,
  IconBinaryTree2,
  IconClock,
  IconCloudDownload,
  IconCopy,
  IconEyeCog,
  IconFolder,
  IconFolderPlus,
  IconRefresh,
  IconSettingsDollar,
  IconTag,
  IconTrashFilled,
} from '@tabler/icons-react';
import React from 'react';
import {
  RenderTreeNodePayload,
  Tree,
  TreeNodeData,
  useTree,
} from 'src/design/molecules/Tree';
import { TreeRow } from 'src/design/molecules/TreeRow';
import { useJobs } from 'src/hooks/useJobs';
import { useRefreshJobWbsFromCtc } from 'src/api/queries/wbs/useWbsMutations';
import { Header } from 'src/design/organisms/Header';
import { getAccountCostCode } from 'src/utils';
import { AccountPanel } from './components/AccountPanel';
import { CTCLinkIndicator } from './components/CTCLinkIndicator';
import { DisplayOptionsModal } from './components/DisplayOptionsModal';
import { EditWbsForm } from './components/EditWbsForm';
import { MixedProgressIndicator } from './components/MixedProgressIndicator';
import { ProgressManagedIndicator } from './components/ProgressManagedIndicator';
import { WbsTreeData } from './components/types';
import { useWbs } from './contexts/WbsContext';

const WBSContent: React.FC = () => {
  const { selectedJob } = useJobs();

  const {
    // State
    treeData,
    selectedTreeRow,
    loading,
    saving,
    jobRootNode,
    isJobRootSelected,
    canCreateRollUpAccount,
    canAddChargeCode,
    canDuplicate,
    canDelete,
    displayOptions,
    wbsDirty,

    // Actions
    refreshWbs,
    onTreeSelect,
    createRollUpAccount,
    addCostCode,
    duplicateCode,
    removeCode,
    createRootNode,
    isRollUpAccount,
    isPlaceholderAccount,
    updateDisplayOptions,
  } = useWbs();

  const refreshFromCtc = useRefreshJobWbsFromCtc(selectedJob?.id ?? -1);

  // WbsContext is an app-level singleton; pick up Progress Viewer edits that
  // pre-warmed too late (or were skipped) when entering this page.
  React.useEffect(() => {
    if (wbsDirty) refreshWbs();
  }, []);

  const [displayOptionsModalOpened, setDisplayOptionsModalOpened] =
    React.useState(false);

  const tree = useTree();

  const getNodeDisplayText = React.useCallback(
    (node: WbsTreeData) => {
      const shouldShowCostCode =
        !displayOptions.hideCostCodes ||
        (displayOptions.alwaysShowCostCodesForCTC && node.isCtcConnected);

      const code = getAccountCostCode(node);

      return node.isArtificial || !shouldShowCostCode || !code
        ? node.name
        : `${code} - ${node.name}`;
    },
    [displayOptions.hideCostCodes, displayOptions.alwaysShowCostCodesForCTC],
  );

  const { treeNodes, nodesById, parentById } = React.useMemo(() => {
    const nodes = new Map<string, WbsTreeData>();
    const parents = new Map<string, string>();

    const toTreeNodeData = (node: WbsTreeData): TreeNodeData => {
      nodes.set(node.id, node);
      const children = node.children || [];
      children.forEach((child) => parents.set(child.id, node.id));
      return {
        value: node.id,
        label: getNodeDisplayText(node),
        children: children.map(toTreeNodeData),
      };
    };

    return {
      treeNodes: jobRootNode ? [toTreeNodeData(jobRootNode)] : [],
      nodesById: nodes,
      parentById: parents,
    };
  }, [jobRootNode, getNodeDisplayText]);

  // Mantine's Tree seeds expandedState from its own effect; child effects run
  // before parent effects, so the keys exist by the time this one fires.
  const expandedAllRef = React.useRef(false);
  React.useEffect(() => {
    if (expandedAllRef.current || treeData.length === 0) return;
    expandedAllRef.current = true;
    tree.expandAllNodes();
  }, [treeData.length]);

  React.useEffect(() => {
    tree.setSelectedState(selectedTreeRow ? [selectedTreeRow.id] : []);
  }, [selectedTreeRow?.id]);

  React.useEffect(() => {
    if (!selectedTreeRow) return;

    const ancestors: string[] = [];
    let parentId = parentById.get(selectedTreeRow.id);
    while (parentId) {
      ancestors.push(parentId);
      parentId = parentById.get(parentId);
    }
    if (ancestors.length === 0) return;

    tree.setExpandedState((current) => {
      const next = { ...current };
      ancestors.forEach((ancestor) => {
        next[ancestor] = true;
      });
      return next;
    });
  }, [selectedTreeRow?.id, parentById]);

  if (!selectedJob) return null;

  const isServiceJob = selectedJob.jobType === 'service';
  const canRefreshFromCtc = !isServiceJob && !!selectedJob.jobNumber?.trim();

  const getTreeNodeIcon = (node?: WbsTreeData) => {
    if (!node || node.isArtificial) {
      return <IconBinaryTree2 size={14} color="var(--mantine-color-bright)" />;
    }
    if (node.archived) {
      return <IconArchive size={14} color="var(--mantine-color-gray-5)" />;
    }
    if (isPlaceholderAccount(node.apply)) {
      return <IconTag size={14} color="var(--mantine-color-gray-5)" />;
    }
    if (node.timesheetsOnly == true) {
      return (
        <IconClock
          size={14}
          color={
            node.active
              ? 'var(--mantine-color-green-6)'
              : 'var(--mantine-color-gray-5)'
          }
        />
      );
    }
    if (isRollUpAccount(node)) {
      return (
        <IconFolder
          size={14}
          color={
            node.active
              ? 'var(--mantine-color-yellow-9)'
              : 'var(--mantine-color-gray-5)'
          }
        />
      );
    }
    return (
      <IconSettingsDollar
        size={14}
        color={
          node.active
            ? 'var(--mantine-color-green-6)'
            : 'var(--mantine-color-gray-5)'
        }
      />
    );
  };

  const renderTreeNode = (payload: RenderTreeNodePayload) => {
    const node = nodesById.get(payload.node.value);
    const isPlaceholder = node ? isPlaceholderAccount(node.apply) : false;

    return (
      <TreeRow
        payload={payload}
        icon={getTreeNodeIcon(node)}
        label={
          <Group gap="xs" align="center" wrap="nowrap">
            <Text
              size="sm"
              c="bright"
              fs={isPlaceholder ? 'italic' : undefined}
              truncate
            >
              {payload.node.label}
            </Text>
            {node && (
              <>
                <CTCLinkIndicator node={node} size="xs" />
                <ProgressManagedIndicator node={node} size="xs" />
                <MixedProgressIndicator node={node} size="xs" />
              </>
            )}
          </Group>
        }
        onSelect={(value) => onTreeSelect([value], payload)}
      />
    );
  };

  return (
    <>
      <Header
        title="Work Breakdown Structure"
        helpText="The Work Breakdown Structure (WBS) is a hierarchical decomposition of the project into manageable components. It helps in organizing and tracking the project's scope, costs, and progress."
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'WBS', href: '' },
          ],
        }}
      />

      {isServiceJob ? (
        <Paper p="md">
          <Alert color="blue" title="WBS Not Available for Service Jobs">
            The Work Breakdown Structure (WBS) editor is not available for
            service jobs. Service jobs use a simplified structure that is
            automatically managed by the system.
          </Alert>
        </Paper>
      ) : (
        <Grid>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Paper>
              <Group gap="xs" justify="space-between" mb="md">
                <Group justify="flex-start">
                  {saving ? (
                    <Loader size="sm" />
                  ) : (
                    <>{getTreeNodeIcon(selectedTreeRow)}</>
                  )}

                  <Text fw={600} w="300px" c="bright">
                    {selectedTreeRow?.name || 'WBS Structure'}
                  </Text>
                </Group>

                <Group gap="xs" justify="flex-end">
                  <Tooltip label="Display Options">
                    <BasicActionIcon
                      size="sm"
                      variant="light"
                      onClick={() => setDisplayOptionsModalOpened(true)}
                    >
                      <IconEyeCog />
                    </BasicActionIcon>
                  </Tooltip>

                  <Tooltip label="Refresh WBS">
                    <BasicActionIcon
                      size="sm"
                      variant="light"
                      onClick={refreshWbs}
                    >
                      <IconRefresh />
                    </BasicActionIcon>
                  </Tooltip>

                  {canRefreshFromCtc && (
                    <BasicActionIcon
                      size="sm"
                      variant="light"
                      label="Refresh from CTC"
                      onClick={() => refreshFromCtc.mutate()}
                      loading={refreshFromCtc.isPending}
                    >
                      <IconCloudDownload />
                    </BasicActionIcon>
                  )}

                  <Tooltip label="Add Charge Code" disabled={!canAddChargeCode}>
                    <BasicActionIcon
                      size="sm"
                      variant="light"
                      onClick={addCostCode}
                      disabled={!canAddChargeCode}
                    >
                      <IconSettingsDollar />
                    </BasicActionIcon>
                  </Tooltip>

                  {canDuplicate && (
                    <Tooltip
                      label="Duplicate Charge Code"
                      disabled={!canDuplicate}
                    >
                      <BasicActionIcon
                        size="sm"
                        variant="light"
                        onClick={duplicateCode}
                        disabled={!canDuplicate}
                      >
                        <IconCopy />
                      </BasicActionIcon>
                    </Tooltip>
                  )}

                  <Tooltip label="Delete">
                    <BasicActionIcon
                      size="sm"
                      variant="light"
                      color="red"
                      onClick={removeCode}
                      disabled={!canDelete}
                    >
                      <IconTrashFilled />
                    </BasicActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              {treeData.length > 0 ? (
                <Tree
                  data={treeNodes}
                  tree={tree}
                  renderNode={renderTreeNode}
                />
              ) : (
                <Stack align="center" py="xl">
                  {loading ? (
                    <Loader size="lg" />
                  ) : (
                    <>
                      <Text c="dimmed" ta="center" mb="md">
                        No WBS structure found. Create your first WBS item to
                        get started.
                      </Text>
                      <BasicButton
                        variant="filled"
                        leftSection={<IconFolderPlus />}
                        onClick={createRootNode}
                      >
                        Create Root Roll-Up Account
                      </BasicButton>
                    </>
                  )}
                </Stack>
              )}
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 7 }}>
            {selectedTreeRow && !isJobRootSelected && <AccountPanel />}
            {isJobRootSelected && <EditWbsForm />}
          </Grid.Col>
        </Grid>
      )}

      <DisplayOptionsModal
        opened={displayOptionsModalOpened}
        onClose={() => setDisplayOptionsModalOpened(false)}
        options={displayOptions}
        onOptionsChange={updateDisplayOptions}
      />
    </>
  );
};

export const WBS: React.FC = () => {
  return <WBSContent />;
};
